import { useQuery } from '@tanstack/react-query'
import { ServerIcon, PlusIcon } from '@heroicons/react/24/outline'
import api from '../services/api'

interface Device {
  id: string
  device_code: string
  name: string
  device_type?: string
  ip_address?: string
  status: string
  last_ping?: string
  is_active: boolean
}

export default function Devices() {
  const { data: devices, isLoading } = useQuery<Device[]>({
    queryKey: ['devices'],
    queryFn: async () => {
      const response = await api.get('/devices')
      return response.data
    }
  })

  const getStatusBadge = (status: string) => {
    const colors = {
      online: 'bg-green-100 text-green-800',
      offline: 'bg-red-100 text-red-800',
      maintenance: 'bg-yellow-100 text-yellow-800',
      error: 'bg-red-100 text-red-800',
      unknown: 'bg-gray-100 text-gray-800'
    }
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800'
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Devices</h1>
          <p className="mt-1 text-sm text-gray-500">
            Monitor and manage your project devices
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button className="btn btn-primary">
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Device
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {devices?.map((device) => (
          <div key={device.id} className="card p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center">
                <ServerIcon className="h-10 w-10 text-primary-600" />
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {device.name}
                  </h3>
                  <p className="text-sm text-gray-500">{device.device_code}</p>
                </div>
              </div>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(device.status)}`}>
                {device.status}
              </span>
            </div>

            <div className="mt-4 space-y-2">
              {device.device_type && (
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Type:</span> {device.device_type}
                </p>
              )}
              {device.ip_address && (
                <p className="text-sm text-gray-600">
                  <span className="font-medium">IP:</span> {device.ip_address}
                </p>
              )}
              {device.last_ping && (
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Last Ping:</span> {new Date(device.last_ping).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      {devices?.length === 0 && (
        <div className="text-center py-12">
          <ServerIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No devices</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by adding your first device.
          </p>
        </div>
      )}
    </div>
  )
}